
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Shop } from '@/lib/supabase/shops/types';
import { useToast } from '@/components/ui/use-toast';
import { getCurrentUserOrganizationId } from '@/lib/supabase/products/organization';

export interface ShopsDataHookReturn {
  shops: Shop[];
  isLoading: boolean;
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  filteredShops: Shop[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  refreshShops: () => Promise<void>;
}

export const useShopsData = (): ShopsDataHookReturn => {
  const [shops, setShops] = useState<Shop[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const { toast } = useToast();

  // Récupérer l'ID d'organisation
  useEffect(() => {
    const getOrgId = async () => {
      const orgId = await getCurrentUserOrganizationId();
      if (orgId) {
        setOrganizationId(orgId);
        console.log("useShopsData: Retrieved organization ID:", orgId);
      } else {
        console.error("useShopsData: Failed to get organization ID");
      }
    };
    
    getOrgId();
  }, []);
  
  const fetchShops = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const orgId = organizationId || await getCurrentUserOrganizationId();
      
      if (!orgId) {
        console.error('useShopsData: No organization ID found');
        toast({
          title: "Erreur",
          description: "Impossible de récupérer les boutiques sans identifiant d'organisation",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }
      
      console.log(`useShopsData: Fetching shops for organization: ${orgId}`);
      
      const { data, error } = await supabase
        .from('shops')
        .select('*')
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('useShopsData: Error fetching shops:', error);
        toast({
          title: "Erreur",
          description: "Impossible de récupérer les boutiques",
          variant: "destructive",
        });
        return;
      }
      
      console.log(`useShopsData: Retrieved ${data?.length || 0} shops`);
      
      // Vérifier si chaque boutique a un slug valide
      const shopsWithSlugs = (data || []).map(shop => {
        if (!shop.slug) {
          // Générer un slug basique à partir du nom
          shop.slug = shop.name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '');
        }
        return shop;
      });
      
      setShops(shopsWithSlugs);
    } catch (error) {
      console.error('useShopsData: Error in fetchShops:', error);
      toast({
        title: "Erreur",
        description: "Une erreur s'est produite lors de la récupération des boutiques",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, organizationId]);
  
  // Récupérer les boutiques lors du montage du composant et quand l'ID d'organisation change
  useEffect(() => {
    if (organizationId) {
      fetchShops();
    }
  }, [fetchShops, organizationId]);

  // Filtrer les boutiques en fonction du terme de recherche
  const filteredShops = shops.filter(shop => 
    shop.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return {
    shops,
    isLoading,
    isCollapsed,
    setIsCollapsed,
    filteredShops,
    searchTerm,
    setSearchTerm,
    refreshShops: fetchShops
  };
};
